import { LoaderController, LOADER_TIMING } from './loaderController.mjs'

export const LOADER_ID = 'fullscreen-loader'
export const STYLE_ID = 'fullscreen-loader-style'

class LoaderRuntime {
	constructor({ documentRef, setTimer, clearTimer, now }) {
		this.document = documentRef
		this.setTimer = setTimer
		this.clearTimer = clearTimer
		this.now = now
		this.showTimer = null
		this.hideTimer = null
		this.visibleSince = 0
		this.controller = new LoaderController({
			setTimer,
			clearTimer,
			now,
			onChange: (snapshot, event) => this.sync(snapshot, event),
		})
	}

	show(message, options) {
		return this.controller.show(message, options)
	}

	hide(token) {
		return this.controller.hide(token)
	}

	clearOwner(owner) {
		const cleared = this.controller.clearOwner(owner)
		if (cleared === 0) {
			this.sync(this.controller.getSnapshot(), { type: 'clear-owner' })
		}
		return cleared
	}

	clearAll() {
		const cleared = this.controller.clearAll()
		if (cleared === 0) {
			this.sync(this.controller.getSnapshot(), { type: 'clear-all' })
		}
		return cleared
	}

	getSnapshot() {
		return this.controller.getSnapshot()
	}

	sync(snapshot, event) {
		if (snapshot.shouldShow) {
			this.cancelHideTimer()

			const loader = this.getLoader()
			if (loader) {
				this.updateMessage(loader, snapshot.message)
				return
			}

			if (snapshot.immediate) {
				this.cancelShowTimer()
				this.render(snapshot.message)
				return
			}

			if (this.showTimer === null) {
				this.showTimer = this.setTimer(() => {
					this.showTimer = null
					const latest = this.controller.getSnapshot()
					if (latest.shouldShow) {
						this.render(latest.message)
					}
				}, LOADER_TIMING.SHOW_DELAY)
			}
			return
		}

		this.cancelShowTimer()

		const loader = this.getLoader()
		if (!loader) {
			this.cancelHideTimer()
			return
		}

		if (
			event.type === 'visual-release' ||
			event.type === 'clear-owner' ||
			event.type === 'clear-all'
		) {
			this.cancelHideTimer()
			loader.remove()
			return
		}

		if (this.hideTimer !== null) return

		const remaining =
			LOADER_TIMING.MIN_SHOW_TIME - (this.now() - this.visibleSince)
		if (remaining <= 0) {
			loader.remove()
			return
		}

		this.hideTimer = this.setTimer(() => {
			this.hideTimer = null
			this.getLoader()?.remove()
		}, remaining)
	}

	render(message) {
		if (this.getLoader()) return

		this.injectKeyframes()
		this.visibleSince = this.now()

		const loader = this.document.createElement('div')
		loader.id = LOADER_ID
		loader.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
    `

		const contentContainer = this.document.createElement('div')
		contentContainer.style.cssText = `
      background-color: white;
      padding: 30px;
      border-radius: 20px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      display: flex;
      flex-direction: column;
      align-items: center;
    `

		const spinner = this.document.createElement('div')
		spinner.className = 'spinner'
		spinner.style.cssText = `
      width: 50px;
      height: 50px;
      border: 3px solid #f3f3f3;
      border-top: 3px solid #3498db;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 20px;
    `

		const messageElement = this.document.createElement('div')
		messageElement.className = 'loader-message'
		messageElement.textContent = message
		messageElement.style.cssText = `
      color: #333;
      font-family: Arial, sans-serif;
      font-size: 16px;
      text-align: center;
    `

		contentContainer.appendChild(spinner)
		contentContainer.appendChild(messageElement)
		loader.appendChild(contentContainer)
		this.document.body.appendChild(loader)
	}

	updateMessage(loader, message) {
		const messageElement = loader.querySelector('.loader-message')
		if (messageElement) {
			messageElement.textContent = message
		}
	}

	injectKeyframes() {
		if (this.document.getElementById(STYLE_ID)) return

		const style = this.document.createElement('style')
		style.id = STYLE_ID
		style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `
		this.document.head.appendChild(style)
	}

	getLoader() {
		return this.document.getElementById(LOADER_ID)
	}

	cancelShowTimer() {
		if (this.showTimer === null) return

		this.clearTimer(this.showTimer)
		this.showTimer = null
	}

	cancelHideTimer() {
		if (this.hideTimer === null) return

		this.clearTimer(this.hideTimer)
		this.hideTimer = null
	}
}

export function createLoaderRuntime({
	documentRef = globalThis.document,
	setTimer = (...args) => globalThis.setTimeout(...args),
	clearTimer = (...args) => globalThis.clearTimeout(...args),
	now = Date.now,
} = {}) {
	return new LoaderRuntime({ documentRef, setTimer, clearTimer, now })
}
